import React, { useState, useEffect } from 'react';
import { isPushSubscribed, getNotificationPermission } from '../lib/oneSignalService';

const ONESIGNAL_APP_ID = 'ce76332a-3f33-4b4b-a4b8-6b241b9df1f8';

const waitForOneSignal = () => {
  return new Promise((resolve) => {
    if (window.OneSignal) {
      resolve(window.OneSignal);
    } else {
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push((OneSignal) => {
        resolve(OneSignal);
      });
    }
  });
};

function NotificationDebug() {
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState({});

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
    console.log(`[Debug] ${message}`);
  };

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    addLog('=== CHECKING CURRENT STATUS ===', 'header');
    
    // Check notification permission
    const permission = Notification.permission;
    addLog(`Notification.permission: ${permission}`, permission === 'granted' ? 'success' : 'warning');
    
    // Check service worker support
    const swSupported = 'serviceWorker' in navigator;
    addLog(`Service Worker supported: ${swSupported}`, swSupported ? 'success' : 'error');
    
    // Check OneSignal
    const oneSignalLoaded = !!window.OneSignal;
    addLog(`OneSignal SDK loaded: ${oneSignalLoaded}`, oneSignalLoaded ? 'success' : 'warning');
    
    // Check service worker registration
    if (swSupported) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        addLog(`Service Worker registered: ${registration.scope}`, 'success');
        addLog(`Active SW: ${registration.active ? 'Yes' : 'No'}`, registration.active ? 'success' : 'warning');
      } else {
        addLog('No Service Worker registered', 'warning');
      }
    }
    
    // Check App ID
    addLog(`OneSignal App ID configured: ${ONESIGNAL_APP_ID ? 'Yes' : 'No'}`, ONESIGNAL_APP_ID ? 'success' : 'error');
    
    // Check if subscribed
    try {
      const subscribed = await isPushSubscribed();
      addLog(`Push subscription status: ${subscribed ? 'Subscribed' : 'Not subscribed'}`, subscribed ? 'success' : 'info');
    } catch (e) {
      addLog(`Could not check subscription: ${e.message}`, 'warning');
    }
    
    setStatus({
      permission,
      swSupported,
      oneSignalLoaded,
      appIdConfigured: !!ONESIGNAL_APP_ID
    });
  };

  const testMethod1 = async () => {
    addLog('=== TEST 1: Standard Notification.requestPermission() ===', 'header');
    try {
      const permission = await Notification.requestPermission();
      addLog(`Result: ${permission}`, permission === 'granted' ? 'success' : 'error');
    } catch (error) {
      addLog(`Error: ${error.message}`, 'error');
    }
  };

  const testMethod2 = async () => {
    addLog('=== TEST 2: Check OneSignal Status ===', 'header');
    try {
      addLog('Waiting for OneSignal...', 'info');
      const OneSignal = await waitForOneSignal();
      addLog('OneSignal SDK ready', 'success');
      
      // Check if subscribed
      const subscribed = await isPushSubscribed();
      addLog(`Subscription status: ${subscribed ? 'Subscribed' : 'Not subscribed'}`, subscribed ? 'success' : 'info');
      
      // Get Player ID if available
      if (subscribed) {
        const playerId = OneSignal.User.PushSubscription.id;
        if (playerId) {
          addLog(`Player ID: ${playerId}`, 'success');
        }
      }
    } catch (error) {
      addLog(`Error: ${error.message}`, 'error');
    }
  };

  const testMethod3 = async () => {
    addLog('=== TEST 3: Show OneSignal Slidedown Prompt ===', 'header');
    try {
      addLog('Waiting for OneSignal...', 'info');
      const OneSignal = await waitForOneSignal();
      addLog('OneSignal ready', 'success');
      
      addLog('Showing slidedown prompt...', 'info');
      await OneSignal.Slidedown.promptPush();
      addLog('Prompt displayed (check browser for slidedown)', 'success');
      
      // Wait a moment and check subscription
      await new Promise(resolve => setTimeout(resolve, 2000));
      const subscribed = await isPushSubscribed();
      addLog(`Subscription result: ${subscribed ? 'Subscribed!' : 'Not subscribed'}`, subscribed ? 'success' : 'info');
      
      if (subscribed) {
        const playerId = await window.OneSignal.getUserId();
        addLog(`SUCCESS! Player ID: ${playerId}`, 'success');
      }
    } catch (error) {
      addLog(`Error: ${error.code} - ${error.message}`, 'error');
    }
  };

  const testMethod4 = async () => {
    addLog('=== TEST 4: Unregister Service Workers & Test ===', 'header');
    try {
      addLog('Step 1: Getting all service worker registrations...', 'info');
      const registrations = await navigator.serviceWorker.getRegistrations();
      addLog(`Found ${registrations.length} service worker(s)`, 'info');
      
      for (let registration of registrations) {
        addLog(`Unregistering: ${registration.scope}`, 'info');
        await registration.unregister();
      }
      addLog('All service workers unregistered', 'success');
      
      addLog('Step 2: Waiting 2 seconds...', 'info');
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      addLog('Step 3: Requesting permission WITHOUT service workers...', 'info');
      const permission = await Notification.requestPermission();
      addLog(`Permission result: ${permission}`, permission === 'granted' ? 'success' : 'error');
      
      if (permission === 'granted') {
        addLog('SUCCESS! The prompt appeared when service workers were removed!', 'success');
        addLog('This means service workers are causing the conflict', 'warning');
        
        // Show a test notification
        new Notification('Test Success!', {
          body: 'Notifications work without service workers',
          icon: '/logo192.png'
        });
      } else {
        addLog('Still failed - not a service worker issue', 'error');
      }
      
      addLog('NOTE: Refresh the page to re-register service workers', 'warning');
    } catch (error) {
      addLog(`Error: ${error.message}`, 'error');
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'success': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      case 'header': return 'text-blue-600 font-bold text-lg mt-4';
      default: return 'text-gray-700';
    }
  };

  return (
    <div className="min-h-screen-safe bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold mb-4">Notification Debug Tool</h1>
          
          {/* Status Panel */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h2 className="text-xl font-semibold mb-3">Current Status</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <strong>Permission:</strong>
                <span className={status.permission === 'granted' ? 'text-green-600 ml-2' : 'text-yellow-600 ml-2'}>
                  {status.permission || 'Checking...'}
                </span>
              </div>
              <div>
                <strong>Service Worker:</strong> 
                <span className={status.swSupported ? 'text-green-600 ml-2' : 'text-red-600 ml-2'}>
                  {status.swSupported ? 'Supported' : 'Not Supported'}
                </span>
              </div>
              <div>
                <strong>OneSignal SDK:</strong> 
                <span className={status.oneSignalLoaded ? 'text-green-600 ml-2' : 'text-yellow-600 ml-2'}>
                  {status.oneSignalLoaded ? 'Loaded' : 'Loading...'}
                </span>
              </div>
              <div>
                <strong>App ID:</strong> 
                <span className={status.appIdConfigured ? 'text-green-600 ml-2' : 'text-red-600 ml-2'}>
                  {status.appIdConfigured ? 'Configured' : 'Missing'}
                </span>
              </div>
            </div>
            <button
              onClick={checkStatus}
              className="mt-3 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Refresh Status
            </button>
          </div>

          {/* Test Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            <button
              onClick={testMethod1}
              className="px-4 py-3 bg-purple-500 text-white rounded hover:bg-purple-600 font-medium"
            >
              Test 1: Request Permission
            </button>
            <button
              onClick={testMethod2}
              className="px-4 py-3 bg-green-500 text-white rounded hover:bg-green-600 font-medium"
            >
              Test 2: Initialize OneSignal
            </button>
            <button
              onClick={testMethod3}
              className="px-4 py-3 bg-blue-500 text-white rounded hover:bg-blue-600 font-medium"
            >
              Test 3: Show Prompt
            </button>
            <button
              onClick={testMethod4}
              className="px-4 py-3 bg-orange-500 text-white rounded hover:bg-orange-600 font-medium"
            >
              Test 4: Unregister SW & Test
            </button>
          </div>

          {/* Logs Panel */}
          <div className="bg-gray-100 rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-xl font-semibold">Logs</h2>
              <button
                onClick={clearLogs}
                className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
              >
                Clear Logs
              </button>
            </div>
            <div className="bg-white rounded border border-gray-300 p-3 max-h-96 overflow-auto font-mono text-sm">
              {logs.length === 0 ? (
                <p className="text-gray-500">No logs yet. Click a test button above.</p>
              ) : (
                logs.map((log, index) => (
                  <div key={index} className={`mb-1 break-all ${getLogColor(log.type)}`}>
                    <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationDebug;
